import {
  downloadS3ObjectBlob,
  getS3ObjectSignedUrl,
} from '@/utils/s3Storage'
import { S3_STORAGE_SCOPE } from '@/config/portalEnvironment'

const MIME_BY_EXT = {
  wav: 'audio/wav',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  ogg: 'audio/ogg',
  webm: 'audio/webm',
}

function inferAudioMimeFromKey(key) {
  const ext = String(key || '').toLowerCase().split('.').pop()
  return MIME_BY_EXT[ext] || 'audio/wav'
}

export function getCallRecordingS3Key(value) {
  const raw = String(value ?? '').trim()
  return raw || null
}

export function extractCallRecordingS3KeyFromUrl(url) {
  const raw = String(url ?? '').trim()
  if (!raw) return null

  try {
    const parsed = new URL(raw)
    const path = decodeURIComponent(parsed.pathname.replace(/^\/+/, ''))
    return path || null
  } catch {
    return null
  }
}

export function resolveCallRecordingS3Key(call = {}) {
  if (!call || typeof call !== 'object') return null

  return (
    getCallRecordingS3Key(call.recording_s3_key) ||
    extractCallRecordingS3KeyFromUrl(call.recording_s3_url) ||
    null
  )
}

export function hasCallRecording(call = {}) {
  return Boolean(resolveCallRecordingS3Key(call))
}

async function loadCallRecordingBlobUrl(s3Key) {
  const blob = await downloadS3ObjectBlob(s3Key, S3_STORAGE_SCOPE.GENERAL)
  if (!blob?.size) return null

  const mime =
    blob.type && blob.type !== 'application/octet-stream'
      ? blob.type
      : inferAudioMimeFromKey(s3Key)
  const buffer = await blob.arrayBuffer()
  const typed = new Blob([buffer], { type: mime })
  return URL.createObjectURL(typed)
}

/**
 * Loads a call recording from S3 using the object key and returns a URL for playback.
 * Prefers a Cognito-signed URL (streams in the audio element); falls back to blob download.
 * @param {string|{ recording_s3_key?: string, recording_s3_url?: string }} source
 * @returns {Promise<string|null>}
 */
export async function loadCallRecordingPlaybackUrl(source) {
  const path =
    typeof source === 'string'
      ? getCallRecordingS3Key(source)
      : resolveCallRecordingS3Key(source)

  if (!path) return null

  try {
    const signedUrl = await getS3ObjectSignedUrl(path, S3_STORAGE_SCOPE.GENERAL)
    if (signedUrl) return signedUrl
  } catch (err) {
    console.warn('Signed URL fetch for call recording failed, trying blob download', err)
  }

  try {
    return await loadCallRecordingBlobUrl(path)
  } catch (err) {
    console.error('Call recording download failed', err)
    return null
  }
}

export function revokeCallRecordingPlaybackUrl(url) {
  if (url && typeof url === 'string' && url.startsWith('blob:')) {
    URL.revokeObjectURL(url)
  }
}
